import React from 'react'
import { supabase } from '../lib/supabase'

type ScreeningRow = { id: number; movie_id: number; start_time: string; available_seats: number; location: string }
type SeatSplitRow = { screening_id: number; silver_total: number; gold_total: number; diamond_total: number; silver_booked: number; gold_booked: number; diamond_booked: number; silver_price?: number; gold_price?: number; diamond_price?: number }

interface OrganizerSeatConfigurationProps {
    splits: SeatSplitRow[]
    setSplits: React.Dispatch<React.SetStateAction<SeatSplitRow[]>>
    latestScreening: ScreeningRow | null
    totals: {
        silver: number
        gold: number
        diamond: number
        sum: number
        silverPct: number
        goldPct: number
        diamondPct: number
    }
    silverPct: number
    setSilverPct: (v: number) => void
    goldPct: number
    setGoldPct: (v: number) => void
    diamondPct: number
    setDiamondPct: (v: number) => void
    layout: { rows: number; cols: number; seats: boolean[][]; seat_numbering_start?: string } | null
    seatMap: Record<string, { booked: boolean; user?: string }>
    onSaveTierPricing: () => Promise<void>
}

export default function OrganizerSeatConfiguration({
    splits,
    setSplits,
    latestScreening,
    totals,
    silverPct,
    setSilverPct,
    goldPct,
    setGoldPct,
    diamondPct,
    setDiamondPct,
    layout,
    seatMap,
    onSaveTierPricing
}: OrganizerSeatConfigurationProps) {

    return (
        <div className="bg-white dark:bg-slate-900/70 rounded-xl shadow-sm p-6 overflow-hidden">
            <h2 className="text-slate-900 dark:text-slate-100 text-xl font-bold mb-4">Seat Configuration</h2>
            {layout ? (
                <div className="space-y-3">
                    <div className="inline-block p-2 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 overflow-x-auto scrollbar-hide">
                        {/* Column Header */}
                        <div className="flex gap-1 mb-1 min-w-max">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-medium text-slate-500" />
                            {Array.from({ length: layout.cols }).map((_, i) => {
                                const numbering = layout.seat_numbering_start || 'top-left'
                                const isRightToLeft = numbering.includes('right')
                                return (
                                    <div key={i} className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-medium text-slate-500">
                                        {isRightToLeft ? layout.cols - i : i + 1}
                                    </div>
                                )
                            })}
                        </div>
                        {/* Rows */}
                        {layout.seats.map((row, rIdx) => {
                            const numbering = layout.seat_numbering_start || 'top-left'
                            const isBottomStart = numbering.includes('bottom')
                            const isRightToLeft = numbering.includes('right')

                            const rowLabelIdx = isBottomStart ? (layout.rows - 1 - rIdx) : rIdx
                            const rowLabel = rowLabelFromIndex(rowLabelIdx)

                            return (
                                <div key={rIdx} className="flex gap-1 mb-1 min-w-max">
                                    {/* Row Label */}
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs font-bold text-slate-700 dark:text-slate-300">
                                        {rowLabel}
                                    </div>
                                    {/* Seats */}
                                    {row.map((present, cIdx) => {
                                        let seatNumber = 0
                                        if (isRightToLeft) {
                                            for (let i = layout.cols - 1; i > cIdx; i--) {
                                                if (row[i]) seatNumber++
                                            }
                                        } else {
                                            for (let i = 0; i < cIdx; i++) { if (row[i]) seatNumber++ }
                                        }

                                        const id = present ? `${rowLabel}${seatNumber + 1}` : `R${rIdx + 1}C${cIdx + 1}`
                                        const booked = present ? !!seatMap[id]?.booked : false
                                        return (
                                            <div
                                                key={id}
                                                title={present ? `${id}${booked ? ' · booked' : ''}` : id}
                                                className={`${present ? (booked ? 'bg-gray-700' : 'bg-green-600') : 'bg-transparent'} w-5 h-5 sm:w-6 sm:h-6 rounded-sm flex items-center justify-center`}
                                            >
                                                {!present && <div className="w-1 h-1 bg-slate-300 rounded-full" />}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div>
            ) : splits.length ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Silver</p>
                            <p className="text-slate-900 dark:text-slate-100 text-base mt-1">{silverPct}%</p>
                            <input type="range" min={0} max={100} value={silverPct} onChange={e => setSilverPct(Number(e.target.value))} className="w-full" />
                            <input
                                type="number"
                                placeholder="€"
                                value={splits.find(s => s.screening_id === latestScreening?.id)?.silver_price ?? 0}
                                onChange={e => {
                                    const v = Number(e.target.value)
                                    setSplits(s => s.map(row => row.screening_id === latestScreening?.id ? { ...row, silver_price: v } : row))
                                }}
                                className="mt-2 w-full px-3 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded"
                            />
                        </div>
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gold</p>
                            <p className="text-slate-900 dark:text-slate-100 text-base mt-1">{goldPct}%</p>
                            <input type="range" min={0} max={100} value={goldPct} onChange={e => setGoldPct(Number(e.target.value))} className="w-full" />
                            <input
                                type="number"
                                placeholder="€"
                                value={splits.find(s => s.screening_id === latestScreening?.id)?.gold_price ?? 0}
                                onChange={e => {
                                    const v = Number(e.target.value)
                                    setSplits(s => s.map(row => row.screening_id === latestScreening?.id ? { ...row, gold_price: v } : row))
                                }}
                                className="mt-2 w-full px-3 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded"
                            />
                        </div>
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Diamond</p>
                            <p className="text-slate-900 dark:text-slate-100 text-base mt-1">{diamondPct}%</p>
                            <input type="range" min={0} max={100} value={diamondPct} onChange={e => setDiamondPct(Number(e.target.value))} className="w-full" />
                            <input
                                type="number"
                                placeholder="€"
                                value={splits.find(s => s.screening_id === latestScreening?.id)?.diamond_price ?? 0}
                                onChange={e => {
                                    const v = Number(e.target.value)
                                    setSplits(s => s.map(row => row.screening_id === latestScreening?.id ? { ...row, diamond_price: v } : row))
                                }}
                                className="mt-2 w-full px-3 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded"
                            />
                        </div>
                    </div>
                    {(silverPct + goldPct + diamondPct !== 100) && (
                        <p className="text-sm text-red-400">Total must be 100%</p>
                    )}
                    <div>
                        <button
                            onClick={async () => {
                                if (!latestScreening) return
                                if (silverPct + goldPct + diamondPct !== 100) return
                                const total = totals.sum
                                const silver = Math.round((total * silverPct) / 100)
                                const gold = Math.round((total * goldPct) / 100)
                                let diamond = total - silver - gold
                                await supabase.from('seatssplitting').update({ silver_total: silver, gold_total: gold, diamond_total: diamond }).eq('screening_id', latestScreening.id)
                                setSplits(s => s.map(row => row.screening_id === latestScreening.id ? { ...row, silver_total: silver, gold_total: gold, diamond_total: diamond } : row))
                                await onSaveTierPricing()
                            }}
                            className="px-4 py-2 bg-indigo-600 text-white rounded"
                        >
                            Save Allocation & Pricing
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-slate-500 dark:text-slate-400 text-sm">No seat layout configured</p>
                </div>
            )}
        </div>
    )
}

const rowLabelFromIndex = (idx: number): string => {
    let n = idx
    let s = ''
    while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s
        n = Math.floor(n / 26) - 1
    }
    return s
}
